import { useEffect } from 'react';

/**
 * Lazily hydrates the figures the renderer left as inert markup.
 *
 * Mermaid, ECharts, and Cytoscape are large, so none of them is in the initial bundle:
 * each module loads only when a document actually contains that kind of figure.
 *
 * Every renderer follows the same contract. It reads its source from a data attribute,
 * marks the element rendered so a re-run cannot duplicate work, and on failure writes a
 * readable message while leaving the original source visible in the details element
 * beneath. A valid figure never shows the error surface, and an invalid one never
 * leaves an empty box.
 */

/** Marks a figure as handled, whatever the outcome. */
function markRendered(element: HTMLElement, wrapper: Element | null, failed: boolean): void {
  element.dataset['atlasRendered'] = 'true';
  wrapper?.classList.toggle('atlas-figure--error', failed);
}

function fail(element: HTMLElement, wrapper: Element | null, message: string): void {
  element.textContent = message;
  markRendered(element, wrapper, true);
}

function pending(container: HTMLElement, attribute: string): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(`[${attribute}]`)).filter(
    (element) => element.dataset['atlasRendered'] !== 'true',
  );
}

/** Semantic colours, read from the active theme so figures match the page. */
function themeColors(): { text: string; muted: string; line: string; brand: string; surface: string } {
  const styles = getComputedStyle(document.body);
  const read = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback;
  return {
    text: read('--text-main', '#111827'),
    muted: read('--text-muted', '#6b7280'),
    line: read('--border', '#e5e7eb'),
    brand: read('--brand-primary', '#4f46e5'),
    surface: read('--bg-surface', '#f9fafb'),
  };
}

export function useRichFigures(html: string, container: HTMLElement | null, dark: boolean) {
  useEffect(() => {
    if (!container) return;
    let cancelled = false;

    const mermaidFigures = pending(container, 'data-atlas-mermaid-source');
    const chartFigures = pending(container, 'data-atlas-echarts-source');
    const graphFigures = pending(container, 'data-atlas-cytoscape-source');

    const disposers: (() => void)[] = [];

    if (mermaidFigures.length > 0) {
      void (async () => {
        const mermaid = (await import('mermaid')).default;
        if (cancelled) return;
        mermaid.initialize({
          startOnLoad: false,
          // Strict security: diagram source may never inject markup or scripts.
          securityLevel: 'strict',
          theme: dark ? 'dark' : 'default',
          fontFamily: 'var(--font-sans)',
        });

        for (const [index, figure] of mermaidFigures.entries()) {
          const wrapper = figure.closest('.atlas-figure');
          const source = figure.dataset['atlasMermaidSource'] ?? '';
          try {
            const { svg } = await mermaid.render(`atlas-mermaid-${Date.now()}-${index}`, source);
            if (cancelled) return;
            figure.innerHTML = svg;
            markRendered(figure, wrapper, false);
          } catch (error) {
            if (cancelled) return;
            fail(
              figure,
              wrapper,
              error instanceof Error ? `Failed to render diagram: ${error.message}` : 'Failed to render diagram.',
            );
          }
        }
      })();
    }

    if (chartFigures.length > 0) {
      void (async () => {
        const echarts = await import('echarts');
        if (cancelled) return;
        const colors = themeColors();

        for (const figure of chartFigures) {
          const wrapper = figure.closest('.atlas-figure');
          const source = figure.dataset['atlasEchartsSource'] ?? '';
          let option: Record<string, unknown>;
          try {
            option = JSON.parse(source) as Record<string, unknown>;
          } catch (error) {
            fail(
              figure,
              wrapper,
              error instanceof Error ? `Failed to read chart data: ${error.message}` : 'Failed to read chart data.',
            );
            continue;
          }

          try {
            figure.style.width = '100%';
            figure.style.minHeight = '280px';
            const chart = echarts.init(figure, undefined, { renderer: 'svg' });
            chart.setOption({
              // The document supplies the data; Atlas supplies the theme, so a chart
              // cannot carry colours that disappear in the other scheme.
              backgroundColor: 'transparent',
              textStyle: { color: colors.text, fontFamily: 'var(--font-sans)' },
              ...option,
            });
            const resize = () => chart.resize();
            window.addEventListener('resize', resize);
            disposers.push(() => {
              window.removeEventListener('resize', resize);
              chart.dispose();
            });
            markRendered(figure, wrapper, false);
          } catch (error) {
            fail(
              figure,
              wrapper,
              error instanceof Error ? `Failed to render chart: ${error.message}` : 'Failed to render chart.',
            );
          }
        }
      })();
    }

    if (graphFigures.length > 0) {
      void (async () => {
        const cytoscape = (await import('cytoscape')).default;
        if (cancelled) return;
        const colors = themeColors();

        for (const figure of graphFigures) {
          const wrapper = figure.closest('.atlas-figure');
          const source = figure.dataset['atlasCytoscapeSource'] ?? '';
          let elements: unknown;
          try {
            elements = JSON.parse(source);
          } catch (error) {
            fail(
              figure,
              wrapper,
              error instanceof Error ? `Failed to read graph data: ${error.message}` : 'Failed to read graph data.',
            );
            continue;
          }

          try {
            figure.style.width = '100%';
            figure.style.minHeight = '280px';
            const instance = cytoscape({
              container: figure,
              elements: elements as cytoscape.ElementDefinition[],
              style: [
                {
                  selector: 'node',
                  style: {
                    'background-color': colors.surface,
                    'border-color': colors.brand,
                    'border-width': 2,
                    label: 'data(label)',
                    color: colors.text,
                    'font-family': 'var(--font-sans)',
                    'font-size': '11px',
                    'text-valign': 'center',
                    'text-halign': 'center',
                    shape: 'round-rectangle',
                    width: 'label',
                    padding: '8px',
                  },
                },
                {
                  selector: 'edge',
                  style: {
                    width: 2,
                    'line-color': colors.line,
                    'target-arrow-color': colors.line,
                    'target-arrow-shape': 'triangle',
                    'curve-style': 'bezier',
                    label: 'data(label)',
                    color: colors.muted,
                    'font-size': '10px',
                  },
                },
              ],
              layout: { name: 'cose', animate: false },
              // A figure inside prose should not swallow the page's scroll.
              userZoomingEnabled: false,
              autoungrabify: true,
            });
            disposers.push(() => instance.destroy());
            markRendered(figure, wrapper, false);
          } catch (error) {
            fail(
              figure,
              wrapper,
              error instanceof Error ? `Failed to render graph: ${error.message}` : 'Failed to render graph.',
            );
          }
        }
      })();
    }

    return () => {
      cancelled = true;
      for (const dispose of disposers) dispose();
    };
  }, [html, container, dark]);
}
